import { USER_ROLES } from "@nlc/shared-types";
import { InfoIcon, StarIcon, SunsetIcon, User2Icon, XIcon } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Link } from "@/components/link";
import { Loader } from "@/components/Loader";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { dateFormatter } from "@/utils/dateFormatter";

import { WhitelabelBadge } from "../../WhitelabelBadge";
import { ImpersonateButton } from "./components/ImpersonateButton";
import { InfoRowAssignedTeams } from "./components/InfoRowAssignedTeams";
import { InfoRowSlots } from "./components/InfoRowSlots";
import { InfoRowStatus } from "./components/InfoRowStatus";
import { InfoRow } from "./components/InforRow";
import { useProjectInformation } from "./hooks/useGetProjectDetails";

function ProjectInfoPanel({
  isOpen,
  onClose,
  projectId,
}: {
  isOpen: boolean;
  onClose: () => void;
  projectId?: number;
}) {
  const { data, isLoading } = useProjectInformation(projectId);
  const { hasRole } = useRoleAccess();

  const showImpersonateButton = hasRole([
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.TEAM_LEADER,
    USER_ROLES.CUSTOMER_SUCCESS,
  ]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-50 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-96 bg-bg-primary shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between pl-6 pr-4 pb-4 pt-4 border-b border-border-primary">
            <h2 className="text-xl font-semibold text-text-primary">
              Account Information
            </h2>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={onClose}
              type="button"
            >
              <XIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {isLoading ? (
            <Loader className="flex-1" />
          ) : (
            <>
              <div className="flex pt-3 pl-6 pr-6">
                {data?.new && (
                  <Badge className="mr-1 mt-1" isRounded variant="blue">
                    <StarIcon className="mr-1" size={14} />
                    New
                  </Badge>
                )}
                {data?.sunset && (
                  <Badge className="mr-1 mt-1" isRounded variant="orange">
                    <SunsetIcon className="mr-1" size={14} />
                    Sunset
                  </Badge>
                )}
                {data?.concierge && (
                  <Badge className="mr-1 mt-1" isRounded variant="success">
                    <User2Icon className="mr-1" size={14} />
                    Concierge
                  </Badge>
                )}
                <WhitelabelBadge
                  className="mr-1 mt-1"
                  role={data?.whitelabelRole}
                />
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <InfoRow
                      label="Account name"
                      link={`/projects/${data?.projectId}`}
                      value={data?.projectName}
                    />
                    <InfoRow label="Owner name" value={data?.ownerName} />
                    <InfoRowAssignedTeams
                      label="Assigned teams"
                      teams={data?.teams ?? []}
                    />
                    <InfoRow label="Plan" value={data?.plan} />
                    <InfoRow
                      label="Billing interval"
                      value={data?.billingInterval}
                    />
                    <InfoRowSlots label="Design slots" slots={data?.slots} />
                    {data?.availableCredits != null && (
                      <InfoRow
                        label="Design Credit(s)"
                        value={data.availableCredits}
                      />
                    )}
                    <InfoRow
                      label="Requests in queue"
                      value={data?.requestsInQueue ?? 0}
                    />
                    <InfoRow
                      label="Renewal date"
                      value={dateFormatter(data?.nextBillingOn)}
                    />
                    <InfoRow
                      label="Cancel date"
                      value={dateFormatter(data?.subscriptionCancelledAt)}
                    />
                    <InfoRowStatus
                      label="Account status"
                      status={data?.status}
                    />
                    <InfoRow label="Timezone" value={data?.timezone} />
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-border-primary space-y-3">
                {showImpersonateButton ? (
                  <ImpersonateButton
                    currentProjectId={Number(projectId)}
                    id={String(data?.ownerId)}
                    isDisabled={false}
                  />
                ) : null}
                <Link
                  className="w-full"
                  href={data?.assetsLinkFolder}
                  target="_blank"
                >
                  <Button className="w-full" variant="primary">
                    Account Designs
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export function ProjectInfoPopup({ projectId }: { projectId?: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const { hasRole } = useRoleAccess();

  const isAllowed = hasRole([
    USER_ROLES.DESIGNER,
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.TEAM_LEADER,
    USER_ROLES.CUSTOMER_SUCCESS,
  ]);

  if (!isAllowed) return null;

  const togglePopup = () => {
    setIsOpen((prev) => {
      if (!prev) setHasOpened(true);

      return !prev;
    });
  };

  return (
    <>
      <button
        className="fixed top-40 right-5 z-40 bg-bg-primary border border-border-primary rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-bg-active cursor-pointer"
        onClick={togglePopup}
        type="button"
      >
        <InfoIcon className="w-5 h-5 text-gray-600" />
      </button>

      {hasOpened && (
        <ProjectInfoPanel
          isOpen={isOpen}
          onClose={togglePopup}
          projectId={projectId}
        />
      )}
    </>
  );
}
